<script>
/* eslint-disable no-unused-vars */
import { Line } from "vue-chartjs";
import { chartDefaults, TooltipLine } from "../../../utilities";

/*
 * This component extends the ChartJS Line and provides the custom options and takes a prop with the actual chart data.
 */
export default {
	extends: Line,
	props: {
		/**
		 * The data object that populates the chart. See documentation above for details.
		 */
		chartData: Object,

		/**
		 * Set the height of the chart in pixels `300px`.
		 */
		chartHeight: String,

		/**
		 * Set the time unit, example `'month'`
		 *
		 * @values day, month, year
		 *
		 */
		timeUnit: String,

		/**
		 * The y-scale axis title
		 */
		yScaleTitle: String,

		/**
		 * **A11Y** Add an accessible description of the chart for screen readers.
		 */
		chartAriaLabel: {
			type: String,
			default: "Line chart"
		},

		/**
		 * Set to `true` to remove any duplicate ticks.
		 */
		removeDuplicateTicks: {
			type: Boolean,
			default: false
		},

		/**
		 * Set the max ticks limit in the dataset.
		 */
		xMaxTicksLimit: {
			type: Number,
			default: 999
		},

		/**
		 * Set the number of decimals to display on the y axis labels.
		 */
		yDecimals: {
			type: Number,
			default: 2
		},

		/**
		 * Set the animation type. `'rebuild`' animates the chart rebuilding. `'off'` turns off animation. Defaults to the standard ChartJS animation when chart.update() is called.
		 *
		 * @values rebuild, off
		 */
		animationType: String
	},
	data: function () {
		return {
			ticksArr: [],
			options: {
				responsive: true,
				maintainAspectRatio: false,
				legend: {
					display: false
				},
				tooltips: {
					enabled: false,
					intersect: false,
					mode: "index"
				},
				scales: {
					xAxes: [
						{
							gridLines: {
								display: false,
								drawBorder: false,
								lineWidth: 2
							},
							beforeUpdate: () => {
								this.ticksArr = [];
							},
							ticks: {
								stacked: true,
								autoSkip: this.removeDuplicateTicks ? false : true,
								maxTicksLimit: this.xMaxTicksLimit,
								maxRotation: 0,
								padding: 8,
								callback: (value, index, values) => {
									let ret;
									let stepSize = this.xMaxTicksLimit
										? Math.ceil(values.length / this.xMaxTicksLimit)
										: 1;
									if (this.removeDuplicateTicks) {
										//Always show first and last tick
										//Last tick must be displayed because if it isn't, the chart width is incorrect and won't sync with bar chart
										if (index === values.length - 1 || index === 0) {
											ret = value;
											this.ticksArr.push(value);
										} else {
											// If tick is a duplicate or if tick index isn't a multiple of step size, hide it
											if (
												this.ticksArr.indexOf(value) >= 0 ||
												(stepSize > 1 && index % stepSize !== 0)
											) {
												ret = null;
											} else {
												ret = value;
												this.ticksArr.push(value);
											}
										}
									} else {
										ret = value;
									}
									return ret;
								}
							},
							type: "time",
							time: {
								unit: this.timeUnit,
								round: "day",
								stepSize: 1,
								displayFormats: {
									month: "MMM [']YY"
								}
							}
						}
					],
					yAxes: [
						{
							scaleLabel: {
								display: this.yScaleTitle ? true : false,
								labelString: this.yScaleTitle
							},
							gridLines: {
								display: true,
								drawBorder: false
							},
							ticks: {
								maxTicksLimit: 4,
								// Include a dollar sign in the ticks
								// default to 2 decimals but extendable to more with yDecimals
								callback: (value) => {
									let parts = value
										.toFixed(this.yDecimals)
										.toString()
										.split(".");
									parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
									return "$" + parts.join(".");
								},
								padding: 20,
								beginAtZero: false
							},
							// Delete highest tick on y-axis
							afterBuildTicks: function (chart) {
								chart.ticks = chart.ticks.slice(1);
							},
							afterFit: function (scaleInstance) {
								scaleInstance.width = 100; // To sync Y axis width
							}
						}
					]
				},
				hover: {
					mode: "index",
					intersect: false,
					onHover: (e) => {
						// Emit an event to the parent to handle what to do on mouse out
						if (e.type === "mouseout") {
							this.$emit("chartMouseOut");
						}
						// Don't do anything if this is from synced simulation
						else if (e.type === "click") {
							return;

							// Send an event to parent to tell it that the index changed
						} else if (
							this.$data._chart.tooltip._active &&
							this.$data._chart.tooltip._active.length
						) {
							if (
								this.$data._chart.tooltip._active[0]._index !== this.activeIndex
							) {
								this.activeIndex = this.$data._chart.tooltip._active[0]._index;
								this.$emit("activeIndexChanged", this.activeIndex);
							}
						}
					}
				}
			},
			activeIndex: 0
		};
	},
	watch: {
		chartHeight: function () {
			this.setHeight(this.chartHeight);
		}
	},
	mounted() {
		this.addPlugin(TooltipLine);
		this.renderChart(this.chartData, this.options);

		// Adds a class to the generated container
		this.$data._chart.canvas.parentNode.classList.add("line-chart");
		this.$data._chart.canvas.setAttribute("role", "img");
		this.$data._chart.canvas.setAttribute("aria-label", this.chartAriaLabel);
	},
	methods: {
		/* Called when the data changes */
		update() {
			if (this.animationType === "rebuild") {
				this.$data._chart.destroy();
				this.renderChart(this.chartData, this.options);
				return;
			}
			if (this.animationType === "off") {
				this.$data._chart.update({ duration: 0 });
				return;
			}
			this.$data._chart.update();
		},
		/* Sets the height of chart on load and breakpoint change */
		setHeight(newHeight) {
			this.$data._chart.canvas.parentNode.style.height = newHeight;
		}
	}
};
</script>
